import { Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { restoreAuthFromNative, endSession } from '@shared/auth/session';
import { startIdleLogout } from '@shared/auth/idleLogout';
import { Capacitor } from '@capacitor/core';
import { hasDualViewAccess, switchToCitizenView } from './utils/switchView';
import { SplashScreen } from '@capacitor/splash-screen';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { ThemeProvider } from '@shared/theme/ThemeContext';
import { useFeatureFlags } from '@shared/flags/FeatureFlagProvider';
import IonicShell from '@shared/ionic/IonicShell';
import IonicRouteView from '@shared/ionic/IonicRouteView';
import IonicStandaloneView from '@shared/ionic/IonicStandaloneView';
import { RoleThemeProvider } from '@shared/theme/RoleThemeProvider';

import { TimeTrackingProvider } from './contexts/TimeTrackingContext';
import { PageMetadataProvider } from './contexts/PageMetadataContext';
import { CustomFieldsCacheProvider } from './contexts/CustomFieldsCacheContext';
import api from './lib/api';
import { initWebVitalsMonitoring } from './utils/webVitalsMonitor';
import HomePage from './pages/HomePage';
import SimpleDashboardPage from './pages/SimpleDashboardPage';
import ProfilePage from './pages/ProfilePage';
import RestrictedAccessPage from './pages/RestrictedAccessPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminPage from './pages/AdminPage';
import EmailTemplatesAdminPage from './pages/EmailTemplatesAdminPage';
import AdminCalendarEmailsPage from './pages/AdminCalendarEmailsPage';
import AdminAudienceGroupsPage from './pages/AdminAudienceGroupsPage';
import AdminAnnouncementsPage from './pages/AdminAnnouncementsPage';
import CalendarUnsubscribePage from './pages/CalendarUnsubscribePage';
import LegislatorsPage from './pages/LegislatorsPage';
import DataImportPage from './pages/DataImportPage';
import DataImportHelpPage from './pages/DataImportHelpPage';
import LegislationPage from './pages/LegislationPage';
import BudgetPage from './pages/BudgetPage';
import BudgetItemDetailPage from './pages/BudgetItemDetailPage';
import BudgetDepartmentDetailPage from './pages/BudgetDepartmentDetailPage';
import LobbyingPage from './pages/LobbyingPage';
import LobbyistDetailPage from './pages/LobbyistDetailPage';
import ClientDetailPage from './pages/ClientDetailPage';
import LobbyistBillsPage from './pages/LobbyistBillsPage';
import LobbyistBillDetailPage from './pages/LobbyistBillDetailPage';
import NonProfitsPage from './pages/NonProfitsPage';
import NonProfitDetailPage from './pages/NonProfitDetailPage';
import CampaignFinancePage from './pages/CampaignFinancePage';
import CampaignFinanceDetailPage from './pages/CampaignFinanceDetailPage';
import BillDetailPage from './pages/BillDetailPage';
import WorkspacesPage from './pages/WorkspacesPage';
import WorkspaceContainer from './components/workspace/WorkspaceContainer';
import CampaignResponsePage from './pages/CampaignResponsePage';
import CampaignShortUrlPage from './pages/CampaignShortUrlPage';
import NoteFeedbackPage from './pages/NoteFeedbackPage';
import ReportShortUrlPage from './pages/ReportShortUrlPage';
import NoteShortUrlPage from './pages/NoteShortUrlPage';
// Lazy load analytics pages (heavy: recharts, jspdf, html2canvas)
const CampaignAnalysisPage = lazy(() => import('./pages/CampaignAnalysisPage'));
const VoterRegistrationPage = lazy(() => import('./pages/VoterRegistrationPage'));
const BallotReturnsPage = lazy(() => import('./pages/BallotReturnsPage'));
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import LegislatorVotesPage from './pages/LegislatorVotesPage';
import CandidatesPage from './pages/CandidatesPage';
const BallotInitiativesPage = lazy(() => import('./pages/BallotInitiativesPage'));
const BallotInitiativeDetailPage = lazy(() => import('./pages/BallotInitiativeDetailPage'));
const PublicBallotInitiativePage = lazy(() => import('./pages/PublicBallotInitiativePage'));
// Unauthenticated public events page — the single events page every entry
// point links to, including the marketing site. Lazy so the citizen fonts and
// theme it carries never enter the main bundle.
const PublicEventsPage = lazy(() => import('./citizen/desktop/pages/PublicEventsPage'));
const SharedBallotPage = lazy(() => import('./citizen/desktop/pages/SharedBallotPage'));
import PersonDetailPage from './pages/PersonDetailPage';
import CalendarDetailPage from './pages/CalendarDetailPage';
import GeneralCalendarPage from './pages/GeneralCalendarPage';
import ElectionsCalendarPage from './pages/ElectionsCalendarPage';
import PublicCalendarPage from './pages/PublicCalendarPage';
import PublicCalendarEventPage from './pages/PublicCalendarEventPage';
const PublicAnalyticsPage = lazy(() => import('./pages/PublicAnalyticsPage'));
const PublicReportPage = lazy(() => import('./pages/PublicReportPage'));
import CalendarShortUrlPage from './pages/CalendarShortUrlPage';
// PublicLegislatorDetailPage and PublicCalendarDetailPage removed - consolidated into preview pages
import PublicBillDetailPage from './pages/PublicBillDetailPage';
import PublicPersonPage from './pages/PublicPersonPage';
import PublicCalendarEventPreviewPage from './pages/PublicCalendarEventPreviewPage';
// Citizen experience — ported v2 app (frontend/web/src/citizen). The unified
// `/login` routes citizens here by accountType; see handleLoginSuccess.
import CitizenApp from './citizen/CitizenApp';
// Citizen desktop web UI (sidebar shell) — lazy so phones/native never pay
// for it. Viewport switch lives in CitizenAppSwitch below.
const CitizenDesktopApp = lazy(() => import('./citizen/desktop/CitizenDesktopApp'));
import { useDeviceContext } from './hooks/useMediaQuery';
import CitizenChrome from './citizen/CitizenChrome';
import CitizenLandingScreen from './citizen/screens/LandingScreen';
import CitizenSignupScreen from './citizen/screens/auth/SignupScreen';
// Org sign-up code join page (org_signup_codes) — standalone /join/:code route.
import JoinOrganizationPage from './pages/JoinOrganizationPage';
import CitizenChooseModePage from './pages/citizen/CitizenChooseModePage';
import CitizenComingSoonPage from './pages/CitizenComingSoonPage';
import SelectViewPage from './pages/SelectViewPage';
import ProTabLayout from './components/pro/ProTabLayout';
import { ProToolbarSlotProvider } from './components/pro/ProToolbarSlot';
import { useProSwipeBack } from './components/pro/useProSwipeBack';
import { useProHardwareBack } from './components/pro/useProHardwareBack';
import {
  ProLoginPage,
  ProHomePage,
  ProNotificationsPage,
  ProProfilePage,
  ProProfileEditPage,
  ProProfileSecurityPage,
  ProProfileNotificationsPage,
  ProProfileDeletePage,
  ProBillsPage,
  ProBillDetailPage,
  ProLegislatorsPage,
  ProLegislatorDetailPage,
  ProWorkspacesPage,
  ProWorkspaceDetailPage,
  ProCalendarPage,
  ProHelpPage,
  ProHelpArticlePage,
  ProContactSupportPage,
} from './pages/pro';
const PublicOutreachPage = lazy(() => import('./pages/PublicOutreachPage'));
const OutreachAnalyticsPage = lazy(() => import('./pages/OutreachAnalyticsPage'));
const RespondPage = lazy(() => import('./pages/RespondPage'));
const OutreachUpdateUnsubscribePage = lazy(() => import('./pages/OutreachUpdateUnsubscribePage'));
import OutreachTemplatesAdminPage from './pages/OutreachTemplatesAdminPage';
import PublicOutreachResultsPage from './pages/PublicOutreachResultsPage';
// Admin Next — greenfield admin suite rebuild (parallel to legacy /admin tools)
import AdminNextLayout from './admin/AdminNextLayout';
import AdminDashboardPage from './admin/pages/AdminDashboardPage';
import AccountsPage from './admin/pages/AccountsPage';
import LegacyOrgDetailRedirect from './admin/components/LegacyOrgDetailRedirect';
import LegacyInitiativeDetailRedirect from './admin/components/LegacyInitiativeDetailRedirect';
import LegacyCandidateDetailRedirect from './admin/components/LegacyCandidateDetailRedirect';
import LegacyLegislatorDetailRedirect from './admin/components/LegacyLegislatorDetailRedirect';
import AccountUserDetailPage from './admin/pages/accounts/AccountUserDetailPage';
import AccountOrganizationDetailPage from './admin/pages/accounts/AccountOrganizationDetailPage';
import AccountWorkspaceDetailPage from './admin/pages/accounts/AccountWorkspaceDetailPage';
import PeoplePage from './admin/pages/people/PeoplePage';
import PeoplePersonDetailPage from './admin/pages/people/PeoplePersonDetailPage';
import ElectionsPage from './admin/pages/ElectionsPage';
import LegislaturePage from './admin/pages/LegislaturePage';
import EngagementPromptsPage from './admin/pages/EngagementPromptsPage';
import NewsFeedsPage from './admin/pages/NewsFeedsPage';
import CommitteesPage from './admin/pages/committees/CommitteesPage';
import CommitteeDetailPage from './admin/pages/committees/CommitteeDetailPage';
import ElectionCycleDetailPage from './admin/pages/elections/ElectionCycleDetailPage';
import AdminCalendarPage from './admin/pages/calendar/AdminCalendarPage';
import InitiativeDetailPage from './admin/pages/elections/InitiativeDetailPage';
import VotingPlaceDetailPage from './admin/pages/elections/VotingPlaceDetailPage';
import LegacyToolPage from './admin/components/LegacyToolPage';
import WorkspaceUnavailablePage from './pages/WorkspaceUnavailablePage';
import HelpCenterPage from './pages/HelpCenterPage';
import DesignSystemPage from './pages/DesignSystemPage';
import OrgReportsPage from './pages/OrgReportsPage';
import NgrReportsPage from './pages/NgrReportsPage';
import ReportsRouteSwitch from './pages/ReportsRouteSwitch';
import LoadingSpinner from '@shared/ui/LoadingSpinner';


/** LaunchDarkly `budget_enabled` — hides route when false. System admins bypass. */
function BudgetRouteGate({ children }: { children: JSX.Element }) {
  const { flags } = useFeatureFlags();
  const userStr = localStorage.getItem('user');
  let isAdmin = false;
  try {
    const u = userStr ? JSON.parse(userStr) : null;
    isAdmin = u?.role?.toLowerCase() === 'system_admin';
  } catch { /* ignore */ }

  if (!flags.budget_enabled && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// On native: /login (and /) redirect to the dedicated pro login page.
// If a token is present, skip login entirely and go to /pro/home.
function NativeHomeRedirect({ children }: { children: JSX.Element }) {
  if (Capacitor.isNativePlatform()) {
    if (localStorage.getItem('token')) {
      return <Navigate to="/pro/home" replace />;
    }
    return <Navigate to="/pro/login" replace />;
  }
  return children;
}

// Auth guard for pro mobile routes — on native redirects to /pro/login, web to /login
function ProRouteGuard({ children }: { children: JSX.Element }) {
  const token = localStorage.getItem('token');
  if (!token) {
    const loginPath = Capacitor.isNativePlatform() ? '/pro/login' : '/login';
    return <Navigate to={loginPath} replace />;
  }
  return children;
}

// Auth guard for citizen routes.
function CitizenRouteGuard({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const accountType = localStorage.getItem('accountType');
  const returnTarget = encodeURIComponent(location.pathname + location.search);

  if (!token) {
    return <Navigate to={`/login?returnUrl=${returnTarget}`} replace />;
  }
  if (accountType !== 'citizen') {
    /* A dual-access user navigating to /citizen/* has stated their intent in
       the URL — switch them straight in rather than detouring through the
       /select-view picker (PRO_CITIZEN_DUAL_ACCESS_PRD §5.4; the login-path
       half of this rule already lives in handleLoginSuccess). The switch is a
       full reload (same semantics as the profile-menu switcher, so themes and
       providers remount clean); accountType is set before it, so the guard
       passes on re-entry. Non-dual accounts keep the picker. */
    if (hasDualViewAccess()) {
      switchToCitizenView(location.pathname + location.search);
      return null;
    }
    return <Navigate to={`/select-view?returnUrl=${returnTarget}`} replace />;
  }
  return children;
}

// Skip /pro/login if already authenticated on native
function ProLoginRedirect({ children }: { children: JSX.Element }) {
  if (Capacitor.isNativePlatform() && localStorage.getItem('token')) {
    return <Navigate to="/pro/home" replace />;
  }
  return children;
}

// /citizen/login is deprecated in favor of unified /login. Keep the route as a
// redirect so existing marketing links and bookmarks continue to work, and
// preserve any query params (returnUrl etc.). A missing returnUrl defaults to
// /citizen/home: arriving here at all expresses citizen intent, and the unified
// login uses a citizen returnUrl to route dual-access accounts into the citizen
// view instead of the pro dashboard (see handleLoginSuccess).
function CitizenLoginRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (!params.get('returnUrl')) {
    params.set('returnUrl', '/citizen/home');
  }
  return <Navigate to={`/login?${params.toString()}`} replace />;
}

// Gate for every /citizen/* route. While `citizen_enabled` is off the citizen
// app is dark and each route renders the coming-soon page instead. Wraps each
// route element individually rather than shadowing the tree with one splat
// route, because React Router v6 ranks by specificity, not declaration order —
// a `/citizen/*` splat would lose to `/citizen/signup` and never match.
function CitizenGate({ children }: { children: React.ReactNode }) {
  const { flags } = useFeatureFlags();
  if (!flags.citizen_enabled) {
    return <CitizenComingSoonPage />;
  }
  return <>{children}</>;
}

// The standalone citizen screens are exactly one viewport tall and enter with
// a slide-up (`cc-slide-up`: translateY(8px) → 0). For those 220ms they
// overflow Ionic's scroll box by 8px, and browsers with classic scrollbars
// (Windows) flash a thick grey bar beside the column. Clipping the transient
// overflow stops the flash; layout height is untouched, so real scrolling on
// short windows still works. Browser-only — the native shell is left as is.
function CitizenEntranceClip({ children }: { children: React.ReactNode }) {
  const { isNative } = useDeviceContext();
  if (isNative) return <>{children}</>;
  return <div style={{ overflowY: 'clip' }}>{children}</div>;
}

// Authenticated citizen app, switched by DELIVERY CHANNEL, not viewport
// (decision 2026-07-11, Documentation/CITIZEN_APP_GAPS_AND_ROADMAP.md D-11):
// every BROWSER — including phone browsers — gets the web UI (its own
// responsiveness covers narrow widths: sidebar ≥1024, TopNav 768–1023,
// hamburger below); the Ionic mobile shell is exclusively for the downloaded
// native (Capacitor) app. Guard is identical on both branches.
function CitizenAppSwitch() {
  const { isNative } = useDeviceContext();
  if (!isNative) {
    return (
      <CitizenRouteGuard>
        <Suspense fallback={<LoadingSpinner size="lg" />}>
          <CitizenDesktopApp />
        </Suspense>
      </CitizenRouteGuard>
    );
  }
  return (
    <IonicStandaloneView>
      <CitizenRouteGuard>
        <CitizenApp />
      </CitizenRouteGuard>
    </IonicStandaloneView>
  );
}

// Health check query - use /health/ping so we always get a fast response with a body (avoids blank Preview/Response when DB is slow or down)
const useHealthCheck = () => {
  return useQuery(['health'], async () => {
    const { data } = await api.get('/health/ping');
    return data;
  }, {
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export { api };

// Sentry-wrapped routing components for automatic performance tracking
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Set user context for Sentry when user data is available
const setSentryUser = (user: any) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
      role: user.role,
    });
  } else {
    Sentry.setUser(null);
  }
};

function App() {
  const [authRestored, setAuthRestored] = useState(false);
  const { isLoading } = useHealthCheck();
  const location = useLocation();

  // Enable iOS WKWebView's native left-edge swipe-back app-wide.
  // No-op on web/Android; the hook gates on Capacitor.getPlatform() === 'ios'.
  useProSwipeBack();

  // Android hardware back → Pro leave policy (sheet dismiss / handlers / history / exit).
  // No-op on web and iOS.
  useProHardwareBack();

  // On native, restore auth from Capacitor Preferences if localStorage was cleared
  useEffect(() => {
    restoreAuthFromNative().then(() => setAuthRestored(true));
  }, []);

  // Hide the native splash only after the startup gate clears, then one paint
  // frame — avoids App Review seeing a white WebView between splash and UI.
  // Safety timeout covers hung health checks so splash never sticks forever.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    let raf = 0;
    const hide = () => {
      if (!cancelled) {
        SplashScreen.hide().catch(() => {});
      }
    };

    const safety = window.setTimeout(hide, 8000);

    if (!isLoading && authRestored) {
      raf = requestAnimationFrame(() => {
        requestAnimationFrame(hide);
      });
    }

    return () => {
      cancelled = true;
      window.clearTimeout(safety);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isLoading, authRestored]);

  // Inactivity auto-logout: sign the user out after SESSION_IDLE_WINDOW (60 min)
  // of no real activity. Pauses automatically when logged out (isActive → false).
  // On fire, best-effort revoke the server session, then clear + route to login.
  useEffect(() => {
    return startIdleLogout({
      isActive: () => !!localStorage.getItem('token'),
      onIdle: () => {
        api.post('/auth/logout').catch(() => {}).finally(() => endSession('expired'));
      },
    });
  }, []);

  // Set Sentry user context on mount and when user changes
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setSentryUser(user);
      } catch {
        // Invalid user data
      }
    }
  }, [location.pathname]); // Re-check on navigation in case user logs in/out

  // Track route context for Sentry (pro vs citizen routes)
  useEffect(() => {
    const isCitizenRoute = location.pathname.startsWith('/citizen');
    const isPublicRoute = location.pathname.startsWith('/public') || 
                          location.pathname.startsWith('/feedback') ||
                          location.pathname.startsWith('/calendar-unsubscribe') ||
                          location.pathname.startsWith('/outreach/public');
    
    // Set Sentry tags to identify which "old" codebase the error came from
    Sentry.setTag('route_type', isCitizenRoute ? 'citizen' : 'pro');
    Sentry.setTag('is_public_route', isPublicRoute);
    Sentry.setTag('ionic_enabled', import.meta.env.VITE_IONIC_ENABLED === '1');
    Sentry.setTag('current_pathname', location.pathname);
    
    // Set context for better grouping in Sentry
    Sentry.setContext('route', {
      pathname: location.pathname,
      route_type: isCitizenRoute ? 'citizen' : 'pro',
      is_public: isPublicRoute,
      ionic_flags_enabled: import.meta.env.VITE_IONIC_ENABLED === '1',
    });
    
    // Add breadcrumb for route changes
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${location.pathname}`,
      level: 'info',
      data: {
        route_type: isCitizenRoute ? 'citizen' : 'pro',
        pathname: location.pathname,
      },
    });
  }, [location.pathname]);

  // Initialize web vitals monitoring on mount (web only — not meaningful in native WebView)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;

    initWebVitalsMonitoring(
      () => location.pathname,
      () => ({
        ionicShellEnabled: import.meta.env.VITE_IONIC_ENABLED === '1',
        ionicRouteEnabled: import.meta.env.VITE_IONIC_ENABLED === '1',
      }),
      () => {
        const user = localStorage.getItem('user');
        if (user) {
          try {
            const parsed = JSON.parse(user);
            return parsed.role || parsed.plan;
          } catch {
            return undefined;
          }
        }
        return undefined;
      }
    );
  // Note: Empty deps array is intentional - we only want to initialize monitoring once on mount.
  // The callback functions capture current values when metrics are collected, not when registered.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading || !authRestored) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
        <LoadingSpinner size="xl" label="Loading application, please wait" />
      </div>
    );
  }

  return (
    <IonicShell>
        <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
          <LoadingSpinner size="xl" label="Loading page..." />
        </div>
      }>
        <SentryRoutes>
      {/* Note feedback page - public, manages its own theme, outside ThemeProvider */}
      <Route
        path="/feedback/:token"
        element={
          <IonicRouteView>
            <NoteFeedbackPage />
          </IonicRouteView>
        }
      />

      {/* All other routes wrapped in ThemeProvider */}
      <Route path="/*" element={
        <ThemeProvider>
          <RoleThemeProvider fallbackRole="pro">
            <CustomFieldsCacheProvider>
              <Routes>
              {/* Public routes (no Layout) */}
              <Route
                path="/"
                element={
                  <NativeHomeRedirect>
                    <IonicRouteView>
                      <LoginPage />
                    </IonicRouteView>
                  </NativeHomeRedirect>
                }
              />
              <Route
                path="/login"
                element={
                  <NativeHomeRedirect>
                    <IonicRouteView>
                      <LoginPage />
                    </IonicRouteView>
                  </NativeHomeRedirect>
                }
              />
              <Route
                path="/login/pro"
                element={
                  <IonicRouteView>
                    <LoginPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/register"
                element={
                  <IonicRouteView>
                    <RegisterPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/calendar-unsubscribe/:token"
                element={
                  <IonicRouteView>
                    <CalendarUnsubscribePage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/accept-invitation/:token"
                element={
                  <IonicRouteView>
                    <AcceptInvitationPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/join/:code"
                element={
                  <IonicRouteView>
                    <JoinOrganizationPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/verify-email/:token"
                element={
                  <IonicRouteView>
                    <VerifyEmailPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <IonicRouteView>
                    <ForgotPasswordPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/reset-password/:token"
                element={
                  <IonicRouteView>
                    <ResetPasswordPage />
                  </IonicRouteView>
                }
              />
              {/* Calendar short URLs - /c/a/:date, /c/o/:id/:date, /c/w/:id/:date, /c/e/:eventId */}
              {/* These must come BEFORE /c/:shortId to prevent conflict */}
              <Route
                path="/c/a/w/:startDate"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/a/m/:startDate"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/o/:orgId/w/:startDate"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/o/:orgId/m/:startDate"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/w/:wsId/w/:startDate"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/w/:wsId/m/:startDate"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/a/:date"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/o/:orgId/:date"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/w/:wsId/:date"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/e/:eventId"
                element={
                  <IonicRouteView>
                    <CalendarShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/c/:shortId"
                element={
                  <IonicRouteView>
                    <CampaignShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/r/:shortId"
                element={
                  <IonicRouteView>
                    <ReportShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/n/:shortId"
                element={
                  <IonicRouteView>
                    <NoteShortUrlPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/reports/:shareToken"
                element={
                  <IonicRouteView>
                    <PublicReportPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/analytics/report/:shareToken"
                element={
                  <IonicStandaloneView>
                    <PublicAnalyticsPage />
                  </IonicStandaloneView>
                }
              />
              <Route
                path="/public/analytics/:shareToken"
                element={
                  <IonicStandaloneView>
                    <PublicAnalyticsPage />
                  </IonicStandaloneView>
                }
              />
              {/* Recipient respond page — public, token + email validated (Phase 5b).
                  IonicRouteView (normal DOM scroll), NOT IonicStandaloneView: the page
                  has no IonPage/IonContent, and the standalone wrapper's fixed inset-0
                  container clips it to the viewport with no way to scroll to the
                  response form. Same pattern as NoteFeedbackPage. */}
              <Route
                path="/respond/:token"
                element={
                  <IonicRouteView>
                    <RespondPage />
                  </IonicRouteView>
                }
              />
              {/* Update-email unsubscribe — public, delivery-id token (Phase 5c) */}
              <Route
                path="/outreach-updates/unsubscribe/:deliveryId"
                element={
                  <IonicStandaloneView>
                    <OutreachUpdateUnsubscribePage />
                  </IonicStandaloneView>
                }
              />
              {/* Public events — unauthenticated; the marketing site links here.
                  No Ionic wrapper: it renders the citizen desktop theme itself
                  (D-11 — every browser gets the web UI). */}
              <Route path="/public/events" element={<PublicEventsPage />} />
              {/* Shared ballot picks — unauthenticated short link (CITIZEN_BALLOT_SHARE_PRD.md §5.4). */}
              <Route path="/b/:shortId" element={<SharedBallotPage />} />
              {/* Public calendar routes */}
              <Route
                path="/public/calendar/all/week/:startDate"
                element={
                  <IonicRouteView>
                    <PublicCalendarPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/org/:orgId/week/:startDate"
                element={
                  <IonicRouteView>
                    <PublicCalendarPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/ws/:workspaceId/week/:startDate"
                element={
                  <IonicRouteView>
                    <PublicCalendarPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/all/month/:startDate"
                element={
                  <IonicRouteView>
                    <PublicCalendarPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/org/:orgId/month/:startDate"
                element={
                  <IonicRouteView>
                    <PublicCalendarPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/ws/:workspaceId/month/:startDate"
                element={
                  <IonicRouteView>
                    <PublicCalendarPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/all/:date"
                element={
                  <IonicRouteView>
                    <PublicCalendarPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/org/:orgId/:date"
                element={
                  <IonicRouteView>
                    <PublicCalendarPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/ws/:workspaceId/:date"
                element={
                  <IonicRouteView>
                    <PublicCalendarPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/events/:eventId"
                element={
                  <IonicRouteView>
                    <PublicCalendarEventPage />
                  </IonicRouteView>
                }
              />
              {/* Public survey runner - no auth required */}
              <Route
                path="/o/:slug"
                element={
                  <IonicRouteView>
                    <PublicOutreachPage />
                  </IonicRouteView>
                }
              />
              {/* Public outreach results - no auth required */}
              <Route
                path="/results/:shortId"
                element={
                  <IonicRouteView>
                    <PublicOutreachResultsPage />
                  </IonicRouteView>
                }
              />
              {/* Campaign response page - public, no auth required, no sidebar */}
              <Route
                path="/campaign-response/:campaignId"
                element={
                  <IonicRouteView>
                    <CampaignResponsePage />
                  </IonicRouteView>
                }
              />


              {/* New public preview pages for Outreach links */}
              <Route
                path="/public/bill-preview/:billNumber"
                element={
                  <IonicRouteView>
                    <PublicBillDetailPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/legislator-preview/:id"
                element={
                  <IonicRouteView>
                    <PublicPersonPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar-event-preview/:eventId"
                element={
                  <IonicRouteView>
                    <PublicCalendarEventPreviewPage />
                  </IonicRouteView>
                }
              />

              {/* Public bill/legislator/calendar pages - show login prompt */}
              <Route
                path="/public/bills/:billNumber"
                element={
                  <IonicRouteView>
                    <PublicBillDetailPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/legislators/:id"
                element={
                  <IonicRouteView>
                    <PublicPersonPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/candidates/:id"
                element={
                  <IonicRouteView>
                    <PublicPersonPage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/ballot-initiatives/:id"
                element={
                  <IonicRouteView>
                    <PublicBallotInitiativePage />
                  </IonicRouteView>
                }
              />
              <Route
                path="/public/calendar/:id"
                element={
                  <IonicRouteView>
                    <PublicCalendarEventPreviewPage />
                  </IonicRouteView>
                }
              />

              {/* Unified post-login view picker for dual-access users */}
              <Route
                path="/select-view"
                element={
                  <IonicRouteView>
                    <SelectViewPage />
                  </IonicRouteView>
                }
              />

              {/* Citizen account routes — unified app, v2 design (no main app Layout).
                  Login is unified at /login; /citizen/login stays as a branded
                  redirect that respects the server's accountType. */}
              <Route
                path="/citizen/login"
                element={
                  <CitizenGate>
                    <CitizenLoginRedirect />
                  </CitizenGate>
                }
              />

              {/* Public citizen landing + signup (no auth guard) */}
              <Route
                path="/citizen"
                element={
                  <CitizenGate>
                    <IonicStandaloneView>
                      <CitizenChrome>
                        <CitizenEntranceClip>
                          <CitizenLandingScreen />
                        </CitizenEntranceClip>
                      </CitizenChrome>
                    </IonicStandaloneView>
                  </CitizenGate>
                }
              />
              <Route
                path="/citizen/signup"
                element={
                  <CitizenGate>
                    <IonicStandaloneView>
                      <CitizenChrome>
                        <CitizenEntranceClip>
                          <CitizenSignupScreen />
                        </CitizenEntranceClip>
                      </CitizenChrome>
                    </IonicStandaloneView>
                  </CitizenGate>
                }
              />

              {/* Dual-access view picker (citizen-styled) */}
              <Route
                path="/citizen/choose-mode"
                element={
                  <CitizenGate>
                    <IonicStandaloneView>
                      <CitizenRouteGuard>
                        <CitizenChooseModePage />
                      </CitizenRouteGuard>
                    </IonicStandaloneView>
                  </CitizenGate>
                }
              />

              {/* Legacy redirect */}
              <Route
                path="/citizen/dashboard"
                element={
                  <CitizenGate>
                    <Navigate to="/citizen/home" replace />
                  </CitizenGate>
                }
              />

              {/* Authenticated citizen app (ported v2): home, ballot, chat,
                  profile, detail screens, and the intake flow. Desktop-width
                  browsers get the sidebar web UI; phones/native the mobile
                  shell (CitizenAppSwitch). */}
              <Route
                path="/citizen/*"
                element={
                  <CitizenGate>
                    <CitizenAppSwitch />
                  </CitizenGate>
                }
              />
              {/* Pro mobile login — standalone page outside the ProTabLayout shell */}
              <Route
                path="/pro/login"
                element={
                  <ProLoginRedirect>
                    <IonicStandaloneView>
                      <ProLoginPage />
                    </IonicStandaloneView>
                  </ProLoginRedirect>
                }
              />
              {/*
               * Pro mobile Contact Support — standalone page outside the
               * ProTabLayout shell so it works for both unauthenticated
               * (entered from /pro/login) and authenticated (entered from
               * /pro/help) flows. The page itself reads localStorage.token
               * to switch between the public and authed feedback endpoints.
               */}
              <Route
                path="/pro/contact-support"
                element={
                  <IonicStandaloneView>
                    <ProContactSupportPage />
                  </IonicStandaloneView>
                }
              />
              {/* Pro mobile routes */}
              <Route
                element={
                  <IonicStandaloneView>
                    <ProRouteGuard>
                      <ProToolbarSlotProvider>
                        <ProTabLayout />
                      </ProToolbarSlotProvider>
                    </ProRouteGuard>
                  </IonicStandaloneView>
                }
              >
                <Route path="/pro/home" element={<ProHomePage />} />
                <Route path="/pro/notifications" element={<ProNotificationsPage />} />
                <Route path="/pro/profile" element={<ProProfilePage />} />
                <Route path="/pro/profile/edit" element={<ProProfileEditPage />} />
                <Route path="/pro/profile/security" element={<ProProfileSecurityPage />} />
                <Route path="/pro/profile/notifications" element={<ProProfileNotificationsPage />} />
                <Route path="/pro/profile/delete" element={<ProProfileDeletePage />} />
                <Route path="/pro/bills" element={<ProBillsPage />} />
                <Route path="/pro/bills/:billId" element={<ProBillDetailPage />} />
                <Route path="/pro/legislators" element={<ProLegislatorsPage />} />
                <Route path="/pro/legislators/:id" element={<ProLegislatorDetailPage />} />
                <Route path="/pro/calendar" element={<ProCalendarPage />} />
                <Route path="/pro/workspaces" element={<ProWorkspacesPage />} />
                <Route path="/pro/workspaces/:id" element={<ProWorkspaceDetailPage />} />
                <Route path="/pro/help" element={<ProHelpPage />} />
                <Route path="/pro/help/:slug" element={<ProHelpArticlePage />} />
              </Route>
              {/* Protected routes (with Layout) */}
              <Route element={
                <IonicRouteView>
                  <PageMetadataProvider>
                    <Layout><Outlet /></Layout>
                  </PageMetadataProvider>
                </IonicRouteView>
              }>
                {/* Routes accessible to all authenticated users */}
                <Route path="home" element={<HomePage />} />
                <Route path="dashboard" element={<SimpleDashboardPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="help" element={<HelpCenterPage />} />
                {/* Internal living style guide — every shared design-system component,
                    rendered live. System admins only (same guard as the admin tools). */}
                <Route path="design-system" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <DesignSystemPage />
                  </ProtectedRoute>
                } />
                <Route path="restricted" element={<RestrictedAccessPage />} />
                <Route path="workspace-unavailable" element={<WorkspaceUnavailablePage />} />
                <Route path="workspaces" element={<WorkspacesPage />} />
                <Route
                  path="workspaces/:id"
                  element={
                    <TimeTrackingProvider>
                      <WorkspaceContainer />
                    </TimeTrackingProvider>
                  }
                />

                <Route path="workspaces/:id/calendar/:date" element={<CalendarDetailPage />} />
                <Route path="calendar" element={<GeneralCalendarPage />} />
                <Route path="elections/calendar" element={<ElectionsCalendarPage />} />
                <Route path="calendar/:date" element={<CalendarDetailPage />} />

                {/* Outreach routes */}
                <Route path="outreach/analytics/:itemId" element={<OutreachAnalyticsPage />} />

                {/* Routes requiring subscription access */}
                {/* Org admins only. The menu entry was the sole gate until
                    requireOrgAdmin existed, so the URL was reachable by anyone
                    with a subscription. requireAdmin would be wrong here — it
                    means SYSTEM admin, and would redirect org admins away. */}
                <Route path="organization-settings" element={
                  <ProtectedRoute requireSubscription={true} requireOrgAdmin={true}>
                    <OrganizationSettingsPage />
                  </ProtectedRoute>
                } />
                <Route path="admin" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <AdminPage />
                  </ProtectedRoute>
                } />
                {/* Legacy Organization/User/Workspace/Analytics tools removed —
                    replaced by the Accounts tool (/admin-next/accounts). Routes
                    redirect so old links and bookmarks keep working. */}
                <Route path="admin/organizations" element={
                  <Navigate to="/admin-next/accounts?tab=organizations" replace />
                } />
                <Route path="admin/organizations/:orgId" element={<LegacyOrgDetailRedirect />} />
                {/* Legacy Candidate/Legislator Management removed 2026-07-26 —
                    replaced by the People tool (/admin-next/people). Routes
                    redirect so bookmarks and inbound links keep working. */}
                <Route path="admin/candidates" element={
                  <Navigate to="/admin-next/people" replace />
                } />
                <Route path="admin/people/:personId" element={<LegacyCandidateDetailRedirect />} />
                <Route path="admin/candidates/person/:personId" element={<LegacyCandidateDetailRedirect />} />
                {/* These two resolve the target person via an admin API call, so they
                    keep the guard rather than relying on a 403 to bounce the user. */}
                <Route path="admin/candidates/:candidateId" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <LegacyCandidateDetailRedirect />
                  </ProtectedRoute>
                } />
                <Route path="admin/legislators/:legislatorId" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <LegacyLegislatorDetailRedirect />
                  </ProtectedRoute>
                } />
                {/* Legacy Election Cycles, Offices and Ballot Initiatives, replaced by
                    the Elections tool (/admin-next/elections). Routes redirect so old
                    links and bookmarks keep working. */}
                <Route path="admin/election-calendars" element={
                  <Navigate to="/admin-next/elections?tab=cycles" replace />
                } />
                <Route path="admin/offices" element={
                  <Navigate to="/admin-next/elections?tab=offices" replace />
                } />
                <Route path="admin/ballot-initiatives" element={
                  <Navigate to="/admin-next/elections?tab=initiatives" replace />
                } />
                <Route path="admin/ballot-initiatives/:id" element={<LegacyInitiativeDetailRedirect />} />
                <Route path="admin/users" element={
                  <Navigate to="/admin-next/accounts?tab=users" replace />
                } />
                <Route path="admin/workspaces" element={
                  <Navigate to="/admin-next/accounts?tab=workspaces" replace />
                } />
                <Route path="admin/data-import" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <DataImportPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/email-templates" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <EmailTemplatesAdminPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/calendar-emails" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <AdminCalendarEmailsPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/audience-groups" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <AdminAudienceGroupsPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/announcements" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <AdminAnnouncementsPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/outreach-templates" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <OutreachTemplatesAdminPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/analytics" element={
                  <Navigate to="/admin-next/accounts?tab=activity" replace />
                } />
                <Route path="admin/data-import/help" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <DataImportHelpPage />
                  </ProtectedRoute>
                } />
                <Route
                  path="admin/district-maps"
                  element={
                    <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                      <Navigate to="/admin/data-import?tab=district-maps" replace />
                    </ProtectedRoute>
                  }
                />
                {/* Admin Next — greenfield admin suite. One guard for the whole
                    tree; tools not yet rebuilt render a registry-driven stub
                    that links to the legacy tool. */}
                <Route path="admin-next" element={
                  <ProtectedRoute requireSubscription={true} requireAdmin={true}>
                    <AdminNextLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<AdminDashboardPage />} />
                  <Route path="accounts" element={<AccountsPage />} />
                  <Route path="accounts/users/:userId" element={<AccountUserDetailPage />} />
                  <Route path="accounts/organizations/:orgId" element={<AccountOrganizationDetailPage />} />
                  <Route path="accounts/workspaces/:workspaceId" element={<AccountWorkspaceDetailPage />} />
                  <Route path="people" element={<PeoplePage />} />
                  <Route path="people/:personId" element={<PeoplePersonDetailPage />} />
                  <Route path="elections" element={<ElectionsPage />} />
                  <Route path="legislature" element={<LegislaturePage />} />
                  <Route path="calendar" element={<AdminCalendarPage />} />
                  <Route path="engagement-prompts" element={<EngagementPromptsPage />} />
                  <Route path="news-feeds" element={<NewsFeedsPage />} />
                  <Route path="committees" element={<CommitteesPage />} />
                  <Route path="committees/:coId" element={<CommitteeDetailPage />} />
                  <Route path="elections/cycles/:calendarId" element={<ElectionCycleDetailPage />} />
                  <Route path="elections/initiatives/:initiativeId" element={<InitiativeDetailPage />} />
                  <Route path="elections/locations/:placeId" element={<VotingPlaceDetailPage />} />
                  <Route path=":toolKey" element={<LegacyToolPage />} />
                </Route>
                <Route path="legislation" element={
                  <ProtectedRoute requireSubscription={true}>
                    <LegislationPage />
                  </ProtectedRoute>
                } />
                <Route path="legislation/:id" element={
                  <ProtectedRoute requireSubscription={true}>
                    <BillDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="budget" element={
                  <BudgetRouteGate>
                    <ProtectedRoute requireSubscription={true}>
                      <BudgetPage />
                    </ProtectedRoute>
                  </BudgetRouteGate>
                } />
                <Route path="budget/departments/:id" element={
                  <BudgetRouteGate>
                    <ProtectedRoute requireSubscription={true}>
                      <BudgetDepartmentDetailPage />
                    </ProtectedRoute>
                  </BudgetRouteGate>
                } />
                <Route path="budget/:id" element={
                  <BudgetRouteGate>
                    <ProtectedRoute requireSubscription={true}>
                      <BudgetItemDetailPage />
                    </ProtectedRoute>
                  </BudgetRouteGate>
                } />
                <Route path="legislators" element={
                  <ProtectedRoute requireSubscription={true}>
                    <LegislatorsPage />
                  </ProtectedRoute>
                } />
                <Route path="legislators/:id" element={
                  <ProtectedRoute requireSubscription={true}>
                    <PersonDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="legislators/:id/votes" element={
                  <ProtectedRoute requireSubscription={true}>
                    <LegislatorVotesPage />
                  </ProtectedRoute>
                } />

                {/* Candidate routes - require subscription */}
                <Route path="candidates" element={
                  <ProtectedRoute requireSubscription={true}>
                    <CandidatesPage />
                  </ProtectedRoute>
                } />
                <Route path="candidates/:id" element={
                  <ProtectedRoute requireSubscription={true}>
                    <PersonDetailPage />
                  </ProtectedRoute>
                } />

                {/* Ballot initiative routes - require subscription */}
                <Route path="ballot-initiatives" element={
                  <ProtectedRoute requireSubscription={true}>
                    <BallotInitiativesPage />
                  </ProtectedRoute>
                } />
                <Route path="ballot-initiatives/:id" element={
                  <ProtectedRoute requireSubscription={true}>
                    <BallotInitiativeDetailPage />
                  </ProtectedRoute>
                } />

                {/* Lobbying routes - require subscription */}
                <Route path="lobbying" element={
                  <ProtectedRoute requireSubscription={true}>
                    <LobbyingPage />
                  </ProtectedRoute>
                } />
                <Route path="lobbyists/:id" element={
                  <ProtectedRoute requireSubscription={true}>
                    <LobbyistDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="clients/:id" element={
                  <ProtectedRoute requireSubscription={true}>
                    <ClientDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="lobbyist-bills" element={
                  <ProtectedRoute requireSubscription={true}>
                    <LobbyistBillsPage />
                  </ProtectedRoute>
                } />
                <Route path="lobbyist-bills/:id" element={
                  <ProtectedRoute requireSubscription={true}>
                    <LobbyistBillDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="non-profits" element={
                  <ProtectedRoute requireSubscription={true}>
                    <NonProfitsPage />
                  </ProtectedRoute>
                } />
                <Route path="non-profits/:id" element={
                  <ProtectedRoute requireSubscription={true}>
                    <NonProfitDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="campaign-finance" element={
                  <ProtectedRoute requireSubscription={true}>
                    <CampaignFinancePage />
                  </ProtectedRoute>
                } />
                <Route path="campaign-finance/:id" element={
                  <ProtectedRoute requireSubscription={true}>
                    <CampaignFinanceDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="campaign-analysis/:campaignId" element={
                  <ProtectedRoute requireSubscription={true}>
                    <CampaignAnalysisPage />
                  </ProtectedRoute>
                } />

                {/* SOS Report - lives in legacy reporting */}
                <Route path="sos-report" element={<Navigate to="/reports-legacy" replace />} />

                {/* Reports: legacy by default, NGR only for orgs flagged into
                    next-gen-reporting (2026-08-19 — NGR is not feature-complete
                    for production). ReportsRouteSwitch picks the surface; the
                    backend re-evaluates the same flag on every /api/ngr route.
                    Both surfaces keep a direct URL so the switch works both
                    ways during rollout. */}
                <Route path="reports" element={
                  <ProtectedRoute requireSubscription={true}>
                    <ReportsRouteSwitch />
                  </ProtectedRoute>
                } />
                <Route path="reports-next" element={
                  <ProtectedRoute requireSubscription={true}>
                    <NgrReportsPage />
                  </ProtectedRoute>
                } />
                <Route path="reports-legacy" element={
                  <ProtectedRoute requireSubscription={true}>
                    <OrgReportsPage />
                  </ProtectedRoute>
                } />
                <Route path="voter-registration" element={
                  <ProtectedRoute requireSubscription={true}>
                    <VoterRegistrationPage />
                  </ProtectedRoute>
                } />
                <Route path="ballot-returns" element={
                  <ProtectedRoute requireSubscription={true}>
                    <BallotReturnsPage />
                  </ProtectedRoute>
                } />

              </Route>
              </Routes>
            </CustomFieldsCacheProvider>
          </RoleThemeProvider>
        </ThemeProvider>
      } />
      </SentryRoutes>
      </Suspense>
    </IonicShell>
  );
}

export default App; 